import {
  useImperativeHandle,
  forwardRef,
  useEffect,
  useState,
  useRef
} from "react"
import { useSelector, useDispatch } from "react-redux"
import {
  getFilter,
  getIsValid,
  getReportStatus,
  getSelectedEquipments,
  setIsValid,
  setReportStatus
} from "src/redux/slicers/reportSlice"
import clsx from "clsx"
import { ACTIONS } from "src/constants"
import moment from "moment"
import { useMutation } from "src/hooks/APIHooks"
import { generateEquipmentPerformanceReport } from "../graphql"
import { REPORT_STATE } from "../constants"
import { downloadFileUsingS3 } from "src/utils/CommonMethods"
import { selectBuildingSummary } from "src/redux/slicers/buildingSummarySlice"
import { RECHECK_INTERVAL, RETRY_COUNT } from "src/constants/common"
import { SecondaryNav, LeftPanel, Typography } from "src/blitz"
import Checkbox from "src/blitz/Checkbox/Checkbox"
import EquipmentList from "../ChillerReport/EquipmentList"
import styles from "./TrsReport.module.scss"
import { TranslateComponent } from "src/common/translations"

const CHILLER_PROFILE_SECTION_KEY = "ChillerProfile"
const CHILLER_PROFILE_SECTION_LABEL = "Chiller Profile"

const CHILLER_UNIT_CONFIGURATION_SECTION_KEY = "ChillerUnitConfiguration"
const CHILLER_UNIT_CONFIGURATION_SECTION_LABEL = "Chiller Unit Configuration"

const TRS_SECTION_KEY = "CompressorOperationalData"
const TRS_SECTION_LABEL = "TRS Chiller Analysis"

const TRS_REPORT_SECTIONS = [
  {
    key: CHILLER_PROFILE_SECTION_KEY,
    label: CHILLER_PROFILE_SECTION_LABEL
  },
  {
    key: CHILLER_UNIT_CONFIGURATION_SECTION_KEY,
    label: CHILLER_UNIT_CONFIGURATION_SECTION_LABEL
  },
  {
    key: TRS_SECTION_KEY,
    label: TRS_SECTION_LABEL
  }
]

const TrsReport = forwardRef((props: any, ref: any) => {
  const { selectedLanguage, selectedMetric } = props

  useImperativeHandle(ref, () => ({
    onClickGenerate: generateReportHandler
  }))

  const isExportingToCloudOnly = useRef(false)
  const dispatch = useDispatch()
  const { data: buildingSummary } = useSelector(selectBuildingSummary)
  const selectedEquipments: any = useSelector(getSelectedEquipments)
  const { cancelled, error } = useSelector(getReportStatus)
  const [reportInfo, setReportInfo] = useState(REPORT_STATE)
  const { startDate, endDate } = useSelector(getFilter)
  const isValid = useSelector(getIsValid)

  const [selectedSections, setSelectedSections] = useState<
    Record<string, boolean>
  >(() =>
    TRS_REPORT_SECTIONS.reduce(
      (acc, section) => ({ ...acc, [section.key]: true }),
      {}
    )
  )

  useEffect(() => {
    dispatch(
      setIsValid(
        Object?.keys(selectedEquipments)?.length > 0 &&
          Object.values(selectedSections).some(Boolean)
      )
    )
  }, [selectedEquipments, selectedSections])

  useEffect(() => {
    let timeout = null
    if (cancelled || !reportInfo.url || error) {
      clearTimeout(timeout)
    } else if (reportInfo.retryCount === 0) {
      timeout = setTimeout(() => checkReportIsFound(), 0)
    } else {
      timeout = setTimeout(() => checkReportIsFound(), RECHECK_INTERVAL)
    }
    return () => clearTimeout(timeout)
  }, [cancelled, reportInfo])

  const checkReportIsFound = async () => {
    try {
      const response = await fetch(reportInfo.url)
      if (response?.status === 404) {
        setReportInfo({ ...reportInfo, retryCount: reportInfo.retryCount + 1 })
        if (reportInfo.retryCount + 1 >= RETRY_COUNT) {
          dispatch(setReportStatus(ACTIONS.ERROR))
        }
      } else if (response?.status === 200) {
        dispatch(setReportStatus(ACTIONS.COMPLETE))
        if (!isExportingToCloudOnly.current) {
          const fileName =
            buildingSummary?.name +
            "_" +
            moment(new Date(startDate)).format("MM-DD-YYYY") +
            "_" +
            selectedEquipments?.name +
            "_TRS_Report"
          downloadFileUsingS3(reportInfo.url, fileName)
        }
      } else {
        dispatch(setReportStatus(ACTIONS.ERROR))
      }
    } catch (e) {
      console.log(e)
      dispatch(setReportStatus(ACTIONS.ERROR))
    }
  }

  const { onSubmit: generateReportQuery } = useMutation({
    query: generateEquipmentPerformanceReport,
    onSuccess: (data: any) => {
      if (data?.data?.generateEquipmentPerformanceReport) {
        const { body } = JSON.parse(
          data?.data?.generateEquipmentPerformanceReport
        )
        setReportInfo({ url: body, retryCount: 0 })
      }
    },
    onError: () => {
      dispatch(setReportStatus(ACTIONS.ERROR))
    }
  })

  const generateReportHandler = (saveTo?: string) => {
    if (isValid) {
      dispatch(setReportStatus(ACTIONS.LOADING))
      const formattedStartDate = moment(new Date(startDate))
        .locale("en")
        .format("YYYY-MM-DD")
      const formattedEndDate = moment(new Date(endDate))
        .locale("en")
        .format("YYYY-MM-DD")
      const currentDate = moment(new Date(buildingSummary?.localTime))
        .locale("en")
        .format("YYYY-MM-DD")

      isExportingToCloudOnly.current = saveTo === "cloud"

      generateReportQuery({
        requestBody: JSON.stringify({
          sections: Object.keys(selectedSections).filter(
            (key) => selectedSections[key]
          ),
          bId: buildingSummary?.id,
          bName: buildingSummary?.name,
          location: buildingSummary?.address,
          postalCode: buildingSummary?.postalCode,
          city: buildingSummary?.city,
          state: buildingSummary?.state,
          eId: selectedEquipments?.id,
          eName: selectedEquipments?.name,
          eType: selectedEquipments?.type,
          start_date: formattedStartDate,
          end_date: formattedEndDate,
          current_date: currentDate,
          systemOfMeasure: selectedMetric?.metricCode,
          language: selectedLanguage?.langCode,
          report: "chiller_trs",
          save: saveTo === "cloud" || saveTo === "all" ? "cloud" : "local"
        })
      })
    }
  }

  return (
    <div className={clsx(styles.trsReportWrapper)}>
      <EquipmentList />
      <TrsSections
        selectedSections={selectedSections}
        setSelectedSections={setSelectedSections}
      />
    </div>
  )
})

interface TrsSectionsProps {
  selectedSections: Record<string, boolean>
  setSelectedSections: (
    value:
      | Record<string, boolean>
      | ((prev: Record<string, boolean>) => Record<string, boolean>)
  ) => void
}

const TrsSections = ({
  selectedSections,
  setSelectedSections
}: TrsSectionsProps) => {
  return (
    <div className={styles.trsSectionsWrapper}>
      <div className={styles.sectionPanel}>
        <SecondaryNav withBorder>
          <LeftPanel>
            <TranslateComponent>Select Report Section</TranslateComponent>
          </LeftPanel>
        </SecondaryNav>
        <div className={styles.sectionList}>
          {TRS_REPORT_SECTIONS.map(({ key, label }) => (
            <Checkbox
              key={key}
              checked={selectedSections[key]}
              onChange={(e: any) =>
                setSelectedSections((prev) => ({
                  ...prev,
                  [key]: e.target.checked
                }))
              }
              name={key}
              label={label}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

TrsReport.displayName = "TrsReport"
export default TrsReport

